'use client';

import { useMemo, useRef, useState } from 'react';
import { Box, HStack, VStack, token } from '@terra/ui-styled-system';

export type RangeHistogramBucket = {
  date: string;
  count: number;
};

export type RangeHistogramSelection = {
  startIndex: number;
  endIndex: number;
};

export type RangeHistogramChartProps = {
  data: RangeHistogramBucket[];
  selection?: RangeHistogramSelection;
  onSelectionChange?: (selection: RangeHistogramSelection) => void;
  height?: number;
  startLabel?: string;
  endLabel?: string;
  ariaLabel: string;
};

export function RangeHistogramChart({
  data,
  selection,
  onSelectionChange,
  height = 64,
  startLabel,
  endLabel,
  ariaLabel,
}: RangeHistogramChartProps) {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const [dragAnchor, setDragAnchor] = useState<number | null>(null);
  const [hoverSelection, setHoverSelection] =
    useState<RangeHistogramSelection | null>(null);
  const maxCount = useMemo(
    () => data.reduce((max, bucket) => Math.max(max, bucket.count), 0),
    [data],
  );
  const activeSelection = hoverSelection ?? selection;
  const selectionStart = activeSelection
    ? Math.min(activeSelection.startIndex, activeSelection.endIndex)
    : 0;
  const selectionEnd = activeSelection
    ? Math.max(activeSelection.startIndex, activeSelection.endIndex)
    : -1;

  const isSelected = (index: number) => {
    if (!activeSelection) {
      return false;
    }

    return (
      index >= Math.min(activeSelection.startIndex, activeSelection.endIndex) &&
      index <= Math.max(activeSelection.startIndex, activeSelection.endIndex)
    );
  };

  const commitSelection = (next: RangeHistogramSelection) => {
    onSelectionChange?.({
      startIndex: Math.min(next.startIndex, next.endIndex),
      endIndex: Math.max(next.startIndex, next.endIndex),
    });
  };

  const getPointerIndex = (clientX: number): number => {
    const bounds = chartRef.current?.getBoundingClientRect();

    if (!bounds || data.length === 0) return 0;
    const position = Math.max(
      0,
      Math.min(bounds.width - 1, clientX - bounds.x),
    );

    return Math.min(
      data.length - 1,
      Math.floor((position / bounds.width) * data.length),
    );
  };

  const shiftSelection = (delta: number) => {
    if (!activeSelection || data.length === 0) return;
    const width = selectionEnd - selectionStart;
    const startIndex = Math.max(
      0,
      Math.min(data.length - width - 1, selectionStart + delta),
    );

    commitSelection({ startIndex, endIndex: startIndex + width });
  };

  return (
    <VStack gap={2} alignItems="stretch" w="full">
      <HStack
        ref={chartRef}
        gap="2px"
        alignItems="flex-end"
        w="full"
        position="relative"
        style={{ height: `${height}px` }}
        cursor="col-resize"
        touchAction="none"
        role="slider"
        tabIndex={0}
        aria-label={ariaLabel}
        aria-valuemin={1}
        aria-valuemax={data.length}
        aria-valuenow={Math.max(1, selectionEnd - selectionStart + 1)}
        onKeyDown={(event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            shiftSelection(-1);
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            shiftSelection(1);
          }
        }}
        onPointerDown={(event) => {
          const index = getPointerIndex(event.clientX);

          event.currentTarget.setPointerCapture(event.pointerId);
          setDragAnchor(index);
          setHoverSelection({ startIndex: index, endIndex: index });
        }}
        onPointerMove={(event) => {
          if (dragAnchor === null) return;
          setHoverSelection({
            startIndex: dragAnchor,
            endIndex: getPointerIndex(event.clientX),
          });
        }}
        onPointerUp={(event) => {
          if (dragAnchor === null) return;
          commitSelection({
            startIndex: dragAnchor,
            endIndex: getPointerIndex(event.clientX),
          });
          setDragAnchor(null);
          setHoverSelection(null);
        }}
        onPointerCancel={() => {
          setDragAnchor(null);
          setHoverSelection(null);
        }}
        onPointerLeave={() => {
          if (dragAnchor === null) {
            setHoverSelection(null);
          }
        }}
      >
        {activeSelection && data.length > 0 ? (
          <Box
            position="absolute"
            top="-4px"
            bottom="-2px"
            borderWidth="1px"
            borderStyle="solid"
            borderColor="border.neutral.muted"
            borderRadius="md"
            bg="bg.neutral.subtle"
            pointerEvents="none"
            style={{
              left: `${(selectionStart / data.length) * 100}%`,
              width: `${((selectionEnd - selectionStart + 1) / data.length) * 100}%`,
            }}
          />
        ) : null}
        {data.map((bucket, index) => {
          const heightPercentage =
            maxCount > 0 ? Math.max(4, (bucket.count / maxCount) * 100) : 4;
          const selected = isSelected(index);

          return (
            <Box
              key={bucket.date}
              flex={1}
              h="full"
              display="flex"
              alignItems="flex-end"
              pointerEvents="none"
              position="relative"
              zIndex={1}
            >
              <Box
                w="full"
                borderRadius="2px"
                style={{
                  height: `${heightPercentage}%`,
                  backgroundColor: token(
                    selected ? 'colors.fg.default' : 'colors.bg.neutral.muted',
                  ),
                }}
              />
            </Box>
          );
        })}
      </HStack>
      {startLabel || endLabel ? (
        <HStack
          justifyContent="space-between"
          w="full"
          pt={2}
          borderTopWidth="1px"
          borderTopStyle="solid"
          borderColor="border.neutral.subtle"
        >
          <Box color="fg.muted" fontSize="xs">
            {startLabel}
          </Box>
          <Box color="fg.muted" fontSize="xs">
            {endLabel}
          </Box>
        </HStack>
      ) : null}
    </VStack>
  );
}
